import { TagInfo } from "@/apis/table/types";
import { createExtendedTag } from "@/apis/taxonomy/route";
import { ParentContextItem, SimilarTagType } from "@/apis/taxonomy/types";
import AxisSelect from "@/components/mapping/extend-tag/axis-select";
import {
  ExtensionInputEnLabel,
  ExtensionInputKorLabel,
  TagExtendFormData,
} from "@/components/mapping/extend-tag/common/forms";
import { useExtendForm } from "@/components/mapping/extend/common/forms";
import { FormContainer } from "@/components/mapping/extend/layout";
import { tagExtendFormSchema } from "@/components/mapping/extend/schemas";
import { useTagging } from "@/hooks/mutations/use-tag";
import useRoleOfCell from "@/hooks/queries/use-role-of-cell";
import useDebounce from "@/hooks/use-debounce";
import queries from "@/queries";
import { useMappingStore } from "@/stores/mapping";
import { Button } from "@/styles/ui/button";
import { DataTypes, PeriodTypes, UnitTypes } from "@/types/enums";
import { Dimension } from "@/types/mapping";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { useSelectItem } from "../detail/hooks";
import { tagKey } from "../utils";
import SimilarTagsSection from "./similar-tags-section";
import { ApiError } from "@/apis/errors";

const ExtendTagSection = ({
  dimension,
  onDeleteButtonClick,
}: {
  dimension: Dimension;
  onDeleteButtonClick: () => void;
}) => {
  const { handleClick } = useTagging({
    toast: {
      onSuccess: () => toast.success("확장 태그가 추가되었습니다."),
      onError: () => toast.error("태그 추가에 실패했습니다."),
    },
  });
  const { workbookId, patchSearchCacheAsync } = useExtendForm();
  const selectedFact = useMappingStore((state) => state.fact.selected);
  const { data: getCellOfRoleData } = useRoleOfCell({
    workbookId: workbookId,
    tableId: selectedFact?.table.id,
    cellAddress: selectedFact?.address,
  });
  const [selectedAxis, setSelectedAxis] = useState<ParentContextItem | null>(
    null,
  );
  const queryClient = useQueryClient();

  const addTagToMapping = async (key: string, koLabel: string, id: string) => {
    await patchSearchCacheAsync({
      request: {
        address: selectedFact?.address ?? "",
        dimension: dimension,
        extendedTag: { key: key },
      },
    });
    const tagInfo: TagInfo = {
      name: key.split("_")[1],
      label: koLabel,
      prefix: key.split("_")[0],
      type: dimension === "LINE_ITEM" ? "LINE_ITEM" : "MEMBER",
      id: id,
      parent:
        dimension === "LINE_ITEM"
          ? null
          : {
              id: selectedAxis?.id ?? "",
              name: selectedAxis?.key.split("_")[1] ?? "",
              label: selectedAxis?.label ?? "",
              prefix: selectedAxis?.key.split("_")[0] ?? "",
              type: "AXIS",
            },
      roleKey: getCellOfRoleData?.defRole?.key ?? "",
    };

    await handleClick(tagInfo, false)();
    // 이후 asynchronous logic 있으면 버그 발생한 전력이 있음.
    // 비슷한 버그 발생하면 실제로 기다리는지 체크할 것.

    await queryClient.invalidateQueries({
      queryKey: queries
        .workbook(workbookId!)
        ._ctx.tables(selectedFact?.table.id!)
        ._ctx.taxonomy().queryKey,
    });

    return tagInfo;
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: TagExtendFormData) => {
      if (getCellOfRoleData == null || getCellOfRoleData.lineItemKey === null) {
        return Promise.reject(new Error("getCellOfRoleData is not set"));
      }

      const lineItemInfo =
        dimension === "AXIS"
          ? null
          : {
              dataType: DataTypes.MONETARY,
              periodType: PeriodTypes.DURATION,
              currency: UnitTypes.KRW,
              balance: null,
              decimals: 0,
              precision: 0,
              negated: false,
            };

      return createExtendedTag(workbookId!, {
        koLabel: data.koLabel,
        enLabel: data.enLabel,
        roleKey: getCellOfRoleData?.defRole.key,
        parentTagKey: data.parentTagKey,
        cellAddress: selectedFact?.address ?? "",
        tableId: selectedFact?.table.id ?? "",
        type: dimension === "AXIS" ? "MEMBER" : "LINE_ITEM",
        lineItemInfo: lineItemInfo,
        childTagKey: null,
        childLineItemInfo: null,
      });
    },
    onSuccess: async (data, variables) => {
      try {
        await addTagToMapping(data.key, variables.koLabel, data.id);
        setOnSubmitLoading(false);

        onDeleteButtonClick();
      } catch (error) {
        toast.error("태그 추가에 실패했습니다.");
        setOnSubmitLoading(false);
      }
    },
    onError: (error) => {
      if (error instanceof ApiError && error.statusCode === 409) {
        toast.error("동일한 영문 라벨의 태그가 존재합니다.");
      } else {
        toast.error("태그 추가에 실패했습니다.");
      }
      setOnSubmitLoading(false);
    },
  });

  const form = useForm<TagExtendFormData>({
    resolver: zodResolver(tagExtendFormSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      koLabel: "",
      enLabel: "",
      parentTagKey: "",
    },
  });

  useEffect(() => {
    if (dimension === "LINE_ITEM" && getCellOfRoleData) {
      form.setValue("parentTagKey", getCellOfRoleData.lineItemKey ?? "");
    }
  }, [dimension, getCellOfRoleData, form]);

  const [selectedExistingTag, setSelectedExistingTag] =
    useState<SimilarTagType | null>(null);

  const [onSubmitLoading, setOnSubmitLoading] = useState(false);
  const { handleSelectItemAsync, isLoading } = useSelectItem({
    parentKey: selectedAxis?.key ?? null,
    tagKey:
      tagKey(
        selectedExistingTag?.prefix ?? "",
        selectedExistingTag?.name ?? "",
      ) ?? "",
    roleKey: getCellOfRoleData?.defRole.key ?? "",
    originalRoleKey: getCellOfRoleData?.defRole.key ?? "",
    patchTagCache: false,
  });

  const onSubmit = (data: TagExtendFormData) => {
    setOnSubmitLoading(true);
    if (
      selectedExistingTag &&
      selectedExistingTag.koLabel === data.koLabel &&
      selectedExistingTag.enLabel === data.enLabel
    ) {
      handleSelectItemAsync()
        .then(() => {
          addTagToMapping(
            selectedExistingTag.prefix + "_" + selectedExistingTag.name,
            selectedExistingTag.koLabel,
            selectedExistingTag.id,
          )
            .then((tagData) => {
              setOnSubmitLoading(false);
              toast.success(tagData.label + " 태그가 추가되었습니다.");
              onDeleteButtonClick();
            })
            .catch((error) => {
              setOnSubmitLoading(false);
              toast.error("태그 추가에 실패했습니다.");
            });
        })
        .catch((error) => {
          setOnSubmitLoading(false);
          toast.error("태그 추가에 실패했습니다.");
        });
    } else {
      mutate({
        koLabel: data.koLabel,
        enLabel: data.enLabel,
        parentTagKey: data.parentTagKey,
      });
    }
  };

  const [koLabel, setKoLabel] = useState("");
  const debouncedKoLabel = useDebounce(koLabel, 500);
  const enLabel = useWatch({
    control: form.control,
    name: "enLabel",
  });
  const debouncedEnLabel = useDebounce(enLabel, 500);

  const { data: tagExists, isFetching } = useQuery({
    ...queries
      .tags()
      ._ctx.exists(workbookId!, debouncedKoLabel, debouncedEnLabel, dimension),
    enabled: !!debouncedKoLabel && !!workbookId && !selectedExistingTag,
    placeholderData: (previousData) => previousData,
  });

  return (
    getCellOfRoleData && (
      <div>
        <div className="flex py-[10px] px-2 flex-stretch items-center">
          <div className="flex-1 label-1-normal-semibold flex items-center gap-2">
            <ArrowLeft
              className="p-[6px] cursor-pointer w-[28px] h-[28px]"
              onClick={onDeleteButtonClick}
            />
            {dimension === "LINE_ITEM" ? "라인아이템" : "구성요소"} 확장
          </div>
        </div>
        <FormContainer>
          <FormProvider {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col"
            >
              <div className="flex items-center justify-end px-4">
                <Button
                  className="rounded-[8px] flex items-center justify-center gap-2 py-2 px-4 h-[36px]"
                  disabled={isPending || onSubmitLoading}
                >
                  <>
                    {isPending || onSubmitLoading ? (
                      <Loader2 className="w-[9.33px] h-[9.33px] flex-shrink-0 aspect-square animate-spin" />
                    ) : (
                      <Plus className="w-[9.33px] h-[9.33px] flex-shrink-0 aspect-square" />
                    )}
                    <div className="text-sm leading-1-normal-medium font-medium">
                      확장 태그 추가
                    </div>
                  </>
                </Button>
              </div>
              <div className="flex flex-col gap-4 p-4">
                {/* getCellOfRoleData 가 있다는 건 표가 선택돼 있다는 뜻이지만
                    타입이 그걸 알지 못해서 여기서 한 번 좁힌다. */}
                {dimension === "AXIS" && selectedFact && (
                  <AxisSelect
                    setSelectedAxis={setSelectedAxis}
                    form={form}
                    workbookId={workbookId!}
                    roleKey={getCellOfRoleData.defRole.key}
                    tableId={selectedFact.table.id}
                  />
                )}
                <ExtensionInputKorLabel
                  form={form}
                  koLabel={koLabel}
                  setKoLabel={setKoLabel}
                  tagExists={tagExists?.koLabelConflict ?? false}
                  conflictTag={tagExists?.tagInfo ?? null}
                  setSelectedExistingTag={setSelectedExistingTag}
                  selectedExistingTag={selectedExistingTag}
                />
                <ExtensionInputEnLabel
                  workbookId={workbookId!}
                  selectedExistingTag={selectedExistingTag}
                  setSelectedExistingTag={setSelectedExistingTag}
                  tagExists={tagExists?.enLabelConflict ?? false}
                  roleKey={getCellOfRoleData.defRole.key}
                  form={form}
                  dimension={dimension}
                />
              </div>
            </form>
          </FormProvider>
        </FormContainer>
        <SimilarTagsSection
          form={form}
          conflictTag={tagExists?.tagInfo ?? null}
          selectedExistingTag={selectedExistingTag}
          setSelectedExistingTag={setSelectedExistingTag}
          workbookId={workbookId!}
          dimension={dimension}
          koLabel={koLabel}
          setKoLabel={setKoLabel}
          debouncedKoLabel={debouncedKoLabel}
        />
      </div>
    )
  );
};

export default ExtendTagSection;
